import { ActionTree, MutationTree } from 'vuex'
import { IRootState } from '@/store'
import { IBasketState } from '@/store/modules/basket/interfaces/IBasketState'
import { IBasketAction } from '@/store/modules/basket/interfaces/IBasketAction'
import { IBasketMutation } from '@/store/modules/basket/interfaces/IBasketMutation'
import { BasketMutationTypes } from '@/store/modules/basket/interfaces/mutation-types'
import { IFetchBasketRequest } from '@/store/modules/basket/interfaces/fetchProducts/IFetchBasketRequest'
import { BasketActionTypes } from '@/store/modules/basket/interfaces/action-types'
import { apolloClient } from '@/store/graphql.config'
import { IFetchBasketResponse } from '@/store/modules/basket/interfaces/fetchProducts/IFetchBasketResponse'
import { IBasketProduct } from '@/store/modules/basket/interfaces/IBasketProduct'
import { fetchCustomerBasketQuery } from './queries/fetchCustomerBasket.query'
import { fetchProductsToCartMutation } from './mutations/fetchProductsToCart.mutation'
import { IAddToBasketResponse } from './interfaces/addProductToBasket/IAddToBasketResponse'
import { IAddToBasketRequest } from './interfaces/addProductToBasket/IAddToBasketRequest'
import { IRemoveFromBasketResponse } from './interfaces/removeFromBasket/IRemoveFromBasketResponse'
import { IRemoveFromBasketRequest } from './interfaces/removeFromBasket/IRemoveFromBasketRequest'
import { fetchRemoveCardItemMutation } from './mutations/fetchRemoveCardItem.mutation'
import { fetchUpdateCartItemsMutation } from './mutations/fetchUpdateCartItems.mutation'
import { fetchUpdatePricesMutation } from './mutations/fetchUpdatePrices.mutation'
import { IUpdateItemsRequest } from './interfaces/updateItems/IUpdateItemsRequest'
import { IUpdateItemsResponse } from './interfaces/updateItems/IUpdateItemsResponse'
import { IUpdatePricesRequest } from './interfaces/updatePrices/IUpdatePricesRequest'
import { Empties, IUpdatePricesResponse } from './interfaces/updatePrices/IUpdatePricesResponse'
import { fetchPlaceOrderMutation } from './mutations/fetchPlaceOrder.mutation'
import { IPlaceOrderRequest } from './interfaces/placeOrder/IPlaceOrderRequest'
import { IOrderInformations, IPlaceOrderResponse } from './interfaces/placeOrder/IPlaceOrderResponse'
import { fetchShippingAddressMutation } from './mutations/fetchShippingAddress.mutation'
import { ISetShippingAddressRequest } from './interfaces/setShippingAddress/ISetShippingAddressRequest'
import {
  ISetShippingAddressResponse,
  ShippingAddress
} from './interfaces/setShippingAddress/ISetShippingAddressResponse'
import { fetchShippingMethodMutation } from './mutations/fetchShippingMethod.mutation'
import { ISetShippingMethodRequest } from './interfaces/setShippingMethod/ISetShippingMethodRequest'
import { ISetShippingMethodResponse, ShippingMethod } from './interfaces/setShippingMethod/ISetShippingMethodResponse'
import { fetchCreateBasketMutation } from './mutations/fetchCreateBasket.mutation'
import { ISetEmptyCartResponse } from './interfaces/emptyCart/ISetEmptyCartResponse'
import { fetchCartQuery } from './queries/fetchCartQuery.query'
import { ICartQueryRequest } from './interfaces/cartQuery/ICartQueryRequest'
import {
  BaseCartQueryItem,
  BaseCartQueryResponse,
  ICartQueryResponse,
  LastCustomerSettings
} from './interfaces/cartQuery/ICartQueryResponse'
import { IError } from '@/store/modules/catalog/interfaces/common/IError'
import { fetchTransportTypeMutation } from './mutations/fetchTransportType.mutation'
import { ISetTransportTypeRequest } from './interfaces/fetchTransportType/ISetTransportTypeRequest'
import { ISetTransportTypeResponse } from './interfaces/fetchTransportType/ISetTransportTypeResponse'
import { addSaveOrderForLaterQuery } from './queries/addSaveOrderForLater.query'
import { createSuggestedCartQuery } from './queries/createSuggestedCart.query'
import { IAddSaveOrderForLaterRequest, IAddSaveOrderForLaterResponse } from './interfaces/addSaveOrderForLater'
import { fetchMinCartForCatalogQuery } from './queries/fetchMinCartForCatalogQuery.query'
import { fetchHPCartQuery } from './queries/fetchHPCartQuery.query'
import { fetchCartValuesQuery } from './queries/fetchCartValuesQuery.query'
import { getAvailableDateAPI, getCalendarAvailableDaysAPI, getAvailableHoursAPI } from './api/getAvailableDate'
import { IGetAvailableDateRequest } from './interfaces/getAvailableDates/IGetAvailableDateRequest'
import i18n from '@/i18n'
import { fetchCustomerLastSettingsQuery } from './queries/fetchCustomerLastSettings.queries'
import { IFetchCustomerLastSettingsRequest } from './interfaces/fetchCustomerSettings/IFetchCustomerRequest'
import { IFetchCustomerLastSettingsResponse } from './interfaces/fetchCustomerSettings/IFetchCustomerResponse'
import { ICreateSuggestedCartRequest, ICreateSuggestedCartResponse } from './interfaces/suggestedCart'
import { cloneDeep } from 'lodash'
import { TransportationTypes } from '@/utils/transportTypes'
import { IAddSaveOrderAsPendingRequest, IAddSaveOrderAsPendingResponse } from './interfaces/addSaveOrderAsPending'
import { addSaveOrderAsPendingQuery } from './queries/addSaveOrderAsPending.query'
import { checkATPQuery } from './queries/checkATPQuery'
import ICheckATP from './interfaces/checkATP/ICheckATP'
import ICheckATPResponse from './interfaces/checkATP/ICheckATPResponse'
import { AvailableHoursResponse } from './interfaces/availableHours/IAvailableHoursResponse'
import { IRemoveAllBasketItemsResponse } from './interfaces/removeAllItems/IRemoveAllBasketItemsResponse'
import { IRemoveAllBasketItemsRequest } from './interfaces/removeAllItems/IRemoveAllBasketItemsRequest'
import { fetchRemoveAllCardItemSMutation } from './queries/fetchRemoveAllCardItems.query'

import { IRemoveEmptyFreeBeerResponse } from './interfaces/removeEmptyFreeBeer/IRemoveEmptyFreeBeerResponse'
import { IRemoveEmptyFreeBeerRequest } from './interfaces/removeEmptyFreeBeer/IRemoveEmptyFreeBeerRequest'
import { fetchEmptyFreeBeerQuery } from './queries/fetchEmptyFreeBeer.query'
import { fetchAutopayPaymentMethodsQuery } from './queries/fetchAutopayPaymentMethods'
import { setAutopayPayment } from './mutations/fetchSetAutopayPayment'
import { fetchAutopayRedirection } from './queries/fetchAutopayRedirection'
import { fetchAutopayPaymentStatus } from './queries/fetchAutopayPaymentStatus.query'

const state = (): IBasketState => ({
  id: '',
  emptyCartId: '',
  items: [],
  loading: false,
  error: null,
  query: {} as BaseCartQueryResponse,
  shippingAddress: [],
  shippingMethods: [],
  prices: {} as IUpdatePricesResponse,
  order: {} as IOrderInformations,
  availableDate: '',
  firstAvailableDateCondition: '',
  availableDays: [],
  availableHours: {} as AvailableHoursResponse,
  deliveryDate: '',
  shipmentId: '',
  transportType: '',
  progressBarMsg: '',
  customerSettings: {} as LastCustomerSettings,
  suggestedCart: {},
  empties: [],
  updatedEmpties: [],
  defaultEmpties: [],
  separatedOrder: [],
  showToastDetails: { show: false, productName: '', actionType: '' },
  updatepriceFlag: false,
  priceUpdateFlag: false,
  emptySavedValue: '',
  onTradeCartUser: {},
  deliveryHour: '',
  availableAutopayPaymentMethods: []
})

const getters = {
  progressBarMessage: (state: IBasketState) => {
    const progressBar = state.query?.progress_bar_data
    const emptyTypes = ['emptiespickup_emptiespickup', 'emptiesfulltruck_emptiesfulltruck']

    if (progressBar && state.query) {
      if (emptyTypes.includes(state.query?.delivery_method)) {
        if (progressBar?.iTotalCartPalletQty < progressBar?.iMinimumOrderPallet) {
          return i18n.global.t('The minimum quantity of pallet has not been reached')
        }
        if (progressBar?.iTotalCartPalletQty > progressBar?.iMaximumOrderPallet) {
          return i18n.global.t('The maximum number of pallets has been exceeded.')
        }
      } else if (
        state.query?.delivery_method === 'flatrate_flatrate' ||
        state.query?.delivery_method === 'emptiesfulltruck_emptiesfulltruck'
      ) {
        if (
          progressBar?.iTotalCartPalletQty < progressBar?.iMinimumOrderPallet &&
          progressBar?.iTotalCartWeight < progressBar?.iMinimumOrderWeight
        ) {
          return i18n.global.t('The minimum quantity of pallet has not been reached')
        }
        if (progressBar?.iTotalCartWeight > progressBar?.iMaximumOrderWeight) {
          return i18n.global.t('The maximum weight of order has been exceeded.')
        }
        if (progressBar?.iTotalCartPalletQty > progressBar?.iMaximumOrderPallet) {
          return i18n.global.t('The maximum number of pallets has been exceeded.')
        }
        if (
          progressBar?.iTotalCartPalletQty >= progressBar?.iMinimumOrderPallet &&
          progressBar?.iTotalCartPalletQty < progressBar?.iMaximumOrderPallet &&
          progressBar?.iTotalCartWeight < progressBar?.iMinimumOrderWeight
        ) {
          return i18n.global.t('The minimum weight of order has not been reached')
        }
        if (
          progressBar?.iTotalCartPalletQty === progressBar?.iMaximumOrderPallet &&
          progressBar?.iTotalCartWeight <= progressBar?.iMaximumOrderWeight
        ) {
          return i18n.global.t('The minimum order capacity has been reached.')
        }
      } else {
        if (progressBar?.iTotalCartPalletQty > progressBar?.iMaximumOrderPallet) {
          return i18n.global.t('The maximum number of pallets has been exceeded.')
        }
      }
    }
    return ''
  },
  progressBarMessageClass: (state: IBasketState) => {
    const progressBar = state.query?.progress_bar_data
    const emptyTypes = ['emptiespickup_emptiespickup', 'emptiesfulltruck_emptiesfulltruck']

    if (progressBar && state.query) {
      if (emptyTypes.includes(state.query?.delivery_method)) {
        if (progressBar?.iTotalCartPalletQty < progressBar?.iMinimumOrderPallet) {
          // Message: 'The minimum quantity of pallet has not been reached'
          return ''
        }
        if (progressBar?.iTotalCartPalletQty > progressBar?.iMaximumOrderPallet) {
          // Message: 'The maximum number of pallets has been exceeded.'
          return 'secondary-red'
        }
      } else if (
        state.query?.delivery_method === 'flatrate_flatrate' ||
        state.query?.delivery_method === 'emptiesfulltruck_emptiesfulltruck'
      ) {
        if (
          progressBar?.iTotalCartPalletQty < progressBar?.iMinimumOrderPallet &&
          progressBar?.iTotalCartWeight < progressBar?.iMinimumOrderWeight
        ) {
          // Message: 'The minimum quantity of pallet has not been reached'
          return ''
        }
        if (progressBar?.iTotalCartWeight > progressBar?.iMaximumOrderWeight) {
          // message: 'The maximum weight of order has been exceeded.'
          return 'secondary-red'
        }
        if (progressBar?.iTotalCartPalletQty > progressBar?.iMaximumOrderPallet) {
          // Message: 'The maximum number of pallets has been exceeded.'
          return 'secondary-red'
        }
        if (
          progressBar?.iTotalCartPalletQty >= progressBar?.iMinimumOrderPallet &&
          progressBar?.iTotalCartPalletQty < progressBar?.iMaximumOrderPallet &&
          progressBar?.iTotalCartWeight < progressBar?.iMinimumOrderWeight
        ) {
          // Message: 'The minimum weight of order has not been reached'
          return ''
        }
        if (
          progressBar?.iTotalCartPalletQty === progressBar?.iMaximumOrderPallet &&
          progressBar?.iTotalCartWeight <= progressBar?.iMaximumOrderWeight
        ) {
          // Message: 'The minimum order capacity has been reached.'
          return 'secondary-green'
        }
      } else {
        if (progressBar?.iTotalCartPalletQty > progressBar?.iMaximumOrderPallet) {
          // Message: 'The maximum number of pallets has been exceeded.'
          return 'secondary-red'
        }
      }
    }
    return ''
  },
  progressBarSuccess (state: IBasketState, getters: any) {
    return getters.progressBarMessage === i18n.global.t('The minimum order capacity has been reached.')
  },
  showProgressBarMessage (state: IBasketState) {
    return state.query?.delivery_method !== TransportationTypes.EMPTY_SMALL
  }
}

const mutations: MutationTree<IBasketState> & IBasketMutation = {
  [BasketMutationTypes.SET_EMPTY_CART] (state, payload: string): void {
    state.emptyCartId = payload
  },
  [BasketMutationTypes.SET_CART_QUERY] (state, payload: BaseCartQueryResponse): void {
    state.query = payload
  },
  [BasketMutationTypes.SET_BASKET_ID] (state, payload: string): void {
    state.id = payload
  },
  [BasketMutationTypes.SET_FIRST_AVAILABLE_DATE] (state, payload: any) {
    state.availableDate = payload
  },
  [BasketMutationTypes.SET_FIRST_AVAILABLE_DATE_CONDITION] (state, payload: any) {
    state.firstAvailableDateCondition = payload
  },
  [BasketMutationTypes.SET_BASKET_ITEMS] (state, payload: IBasketProduct[]): void {
    state.items = payload
  },
  [BasketMutationTypes.SET_ERROR] (state, payload: unknown): void {
    state.error = payload
  },
  [BasketMutationTypes.SET_LOADING] (state, payload: boolean): void {
    state.loading = payload
  },
  [BasketMutationTypes.SET_SHIPPING_ADDRESS] (state, payload: ShippingAddress[]): void {
    state.shippingAddress = payload
  },
  [BasketMutationTypes.SET_SHIPPING_METHODS] (state, payload: ShippingMethod[]): void {
    state.shippingMethods = payload
  },
  [BasketMutationTypes.SET_PRICES] (state, payload: IUpdatePricesResponse): void {
    state.prices = payload
  },
  [BasketMutationTypes.SET_ORDER] (state, payload: IOrderInformations): void {
    state.order = payload
  },
  [BasketMutationTypes.SET_DELIVERY_DATE] (state, payload: string): void {
    state.deliveryDate = payload
  },
  [BasketMutationTypes.SET_SHIPMENT_ID] (state, payload: string): void {
    state.shipmentId = payload
  },
  [BasketMutationTypes.SET_TRANSPORT_TYPE] (state, payload: string): void {
    state.transportType = payload
  },
  [BasketMutationTypes.SET_PROGRESS_BAR] (state, payload: string): void {
    state.progressBarMsg = payload
  },
  [BasketMutationTypes.SET_CUSTOMER_SETTINGS] (state, payload: LastCustomerSettings): void {
    state.customerSettings = payload
  },
  [BasketMutationTypes.SET_EMPTIES] (state, payload: Empties[]): void {
    state.empties = payload
  },
  [BasketMutationTypes.SET_DEFAULT_EMPTIES] (state, payload: Empties[]): void {
    state.defaultEmpties = payload
  },
  [BasketMutationTypes.SET_UPDATED_EMPTIES] (state, payload: Empties[]): void {
    state.updatedEmpties = payload
  },
  [BasketMutationTypes.SET_SEPARATED_ORDER] (state, payload: Empties[]): void {
    state.separatedOrder = payload
  },

  [BasketMutationTypes.SET_TOAST_NOTIFICATION_DETAILS] (state, payload: any): void {
    state.showToastDetails = payload
  },
  [BasketMutationTypes.SET_AVAILABLE_DAYS] (state, payload: string[]) {
    state.availableDays = payload
  },
  [BasketMutationTypes.SET_AVAILABLE_HOUR] (state, payload: AvailableHoursResponse) {
    state.availableHours = payload
  },
  [BasketMutationTypes.SET_UPDATE_PRICE_FLAG] (state, payload: boolean) {
    state.updatepriceFlag = payload
  },
  [BasketMutationTypes.SET_PRICE_UPDATE_FLAG] (state, payload: boolean) {
    state.priceUpdateFlag = payload
  },
  [BasketMutationTypes.SET_UPDATE_EMPTY_VALUE] (state, payload: string) {
    state.emptySavedValue = payload
  },
  [BasketMutationTypes.SET_UPDATE_CART_USER] (state, payload: any) {
    state.onTradeCartUser = payload
  },
  [BasketMutationTypes.SET_DELIVERY_HOUR] (state, payload: any) {
    state.deliveryHour = payload
  },
  [BasketMutationTypes.SET_AVAILABLE_PAYMENT_METHODS] (state, payload: AvailablePaymentMethod[]) {
    state.availableAutopayPaymentMethods = payload
  }

}

const actions: ActionTree<IBasketState, IRootState> & IBasketAction = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async [BasketActionTypes.CREATE_EMPTY_CART] ({ commit }) {
    await apolloClient.mutate<ISetEmptyCartResponse, object>({
      mutation: fetchCreateBasketMutation
    })
  },
  async [BasketActionTypes.FETCH_CUSTOMER_LAST_SETTINGS] ({ commit }, dto: IFetchCustomerLastSettingsRequest) {
    const { data } = await apolloClient.mutate<IFetchCustomerLastSettingsResponse, IFetchCustomerLastSettingsRequest>({
      mutation: fetchCustomerLastSettingsQuery,
      variables: {
        cart_id: dto.cart_id
      }
    })
    if (data) {
      commit(BasketMutationTypes.SET_CUSTOMER_SETTINGS, data?.cart)
    }
  },
  async [BasketActionTypes.GET_FIRST_AVAILABLE_DATE] ({ commit }, dto: IGetAvailableDateRequest) {
    if (dto.ShipToId && dto.store) {
      commit(BasketMutationTypes.SET_LOADING, true)
      const response = await getAvailableDateAPI(dto)
      commit(BasketMutationTypes.SET_FIRST_AVAILABLE_DATE, response.firstAvailableDate)
      localStorage.setItem('deliveryDate', response.firstAvailableDate.split('T')[0])
      commit(BasketMutationTypes.SET_FIRST_AVAILABLE_DATE_CONDITION, response.conditionDate)
      commit(BasketMutationTypes.SET_LOADING, false)
    }
  },
  async [BasketActionTypes.GET_AVAILABLE_DAYS] ({ commit }, dto: IGetAvailableDateRequest) {
    if (dto.ShipToId) {
      commit(BasketMutationTypes.SET_LOADING, true)
      await getCalendarAvailableDaysAPI(dto).then((res) => {
        commit(BasketMutationTypes.SET_AVAILABLE_DAYS, res)
      })
      commit(BasketMutationTypes.SET_LOADING, false)
    }
  },
  async [BasketActionTypes.GET_AVAILABLE_HOUR] ({ commit }, dto: IGetAvailableDateRequest) {
    const response = await getAvailableHoursAPI(dto)
    commit(BasketMutationTypes.SET_AVAILABLE_HOUR, response)
  },
  async [BasketActionTypes.FETCH_CART_QUERY] ({ commit }, dto: ICartQueryRequest) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      const { data } = await apolloClient.mutate<ICartQueryResponse, ICartQueryRequest>({
        mutation: fetchCartQuery,
        variables: {
          cart_id: dto.cart_id
        }
      })

      if (data) {
        data.cart = parseCartQuery(data.cart)
        if (data?.cart?.empties?.length > 0) {
          commit(BasketMutationTypes.SET_EMPTIES, data?.cart?.empties)
        }
      }
      const emptyProductSKU = '5901359003313'
      const items = data?.cart?.items ? data?.cart?.items.filter((item: any) => item.product.sku !== emptyProductSKU) : []
      commit(BasketMutationTypes.SET_CART_QUERY, data?.cart)
      commit(BasketMutationTypes.SET_DELIVERY_DATE, data?.cart?.delivery_date || '')
      commit(BasketMutationTypes.SET_BASKET_ITEMS, items)
      commit(BasketMutationTypes.SET_LOADING, false)
    } catch (e) {
      console.error(e)
      commit(BasketMutationTypes.SET_LOADING, false)
    }
    commit(BasketMutationTypes.SET_LOADING, false)
  },
  async [BasketActionTypes.FETCH_MIN_CART_QUERY_FOR_CATALOG] ({ commit }, dto: ICartQueryRequest) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      const { data } = await apolloClient.mutate<ICartQueryResponse, ICartQueryRequest>({
        mutation: fetchMinCartForCatalogQuery,
        variables: {
          cart_id: dto.cart_id
        }
      })
      if (data) {
        data.cart.grand_total_your_price_details = JSON.parse(data?.cart.grand_total_your_price_details as string)
      }
      commit(BasketMutationTypes.SET_CART_QUERY, data?.cart)
      commit(BasketMutationTypes.SET_DELIVERY_DATE, data?.cart?.delivery_date || '')
      commit(BasketMutationTypes.SET_BASKET_ITEMS, data?.cart?.items)
      commit(BasketMutationTypes.SET_LOADING, false)
    } catch (e) {
      console.error(e)
      commit(BasketMutationTypes.SET_LOADING, false)
    }
    commit(BasketMutationTypes.SET_LOADING, false)
  },

  // fetch cart query on Homepage
  async [BasketActionTypes.FETCH_HP_CART_QUERY] ({ commit }, dto: ICartQueryRequest) {
    commit(BasketMutationTypes.SET_LOADING, true)
    const { data } = await apolloClient.mutate<ICartQueryResponse, ICartQueryRequest>({
      mutation: fetchHPCartQuery,
      variables: {
        cart_id: dto.cart_id
      }
    })
    if (data) {
      data.cart.grand_total_your_price_details = JSON.parse(data?.cart.grand_total_your_price_details as string)
      commit(BasketMutationTypes.SET_CART_QUERY, data?.cart)
      commit(BasketMutationTypes.SET_DELIVERY_DATE, data?.cart?.delivery_date || '')
      commit(BasketMutationTypes.SET_BASKET_ITEMS, data?.cart?.items)
      commit(BasketMutationTypes.SET_LOADING, false)
    }
    commit(BasketMutationTypes.SET_LOADING, false)
  },

  async [BasketActionTypes.FETCH_CART_VALUES] ({ commit }, dto: ICartQueryRequest) {
    commit(BasketMutationTypes.SET_LOADING, true)
    try {
      const { data } = await apolloClient.mutate<ICartQueryResponse, ICartQueryRequest>({
        mutation: fetchCartValuesQuery,
        variables: {
          cart_id: dto.cart_id
        }
      })
      commit(BasketMutationTypes.SET_CART_QUERY, {
        delivery_date: data?.cart.delivery_date,
        delivery_method: data?.cart?.delivery_method,
        ship_to_id: data?.cart?.ship_to_id,
        grand_total_your_price_details: JSON.parse(data?.cart?.grand_total_your_price_details as string),
        progress_bar_data: data?.cart?.progress_bar_data,
        cart_prices: data?.cart?.cart_prices,
        outlet_details: data?.cart.outlet_details
      })
      commit(BasketMutationTypes.SET_BASKET_ITEMS, data?.cart?.items)
      commit(BasketMutationTypes.SET_LOADING, false)
    } catch (e) {
      commit(BasketMutationTypes.SET_LOADING, false)
      console.error(e)
    }
    commit(BasketMutationTypes.SET_LOADING, false)
  },
  async [BasketActionTypes.FETCH_BASKET_ID] ({ commit }) {
    commit(BasketMutationTypes.SET_LOADING, true)
    const { data } = await apolloClient.query<IFetchBasketResponse, IFetchBasketRequest>({
      query: fetchCustomerBasketQuery
    })
    try {
      if (data) {
        commit(BasketMutationTypes.SET_BASKET_ID, data?.customerCart.id)
        commit(BasketMutationTypes.SET_LOADING, false)
      }
    } catch (error) {
      commit(BasketMutationTypes.SET_LOADING, false)
      commit(BasketMutationTypes.SET_ERROR, error as IError[])
      console.error('fetch basket error', error)
    }
  },
  async [BasketActionTypes.FETCH_ADD_TO_BASKET] ({ commit }, dto: IAddToBasketRequest) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      await apolloClient.mutate<IAddToBasketResponse, IAddToBasketRequest>({
        mutation: fetchProductsToCartMutation,
        variables: {
          cart_id: dto.cart_id,
          sku: dto.sku,
          quantity: dto.quantity,
          number_of_layer: dto.number_of_layer,
          number_of_pallet: dto.number_of_pallet,
          selected_options: dto.selected_options
        }
      })
      commit(BasketMutationTypes.SET_LOADING, false)
    } catch (error: any) {
      console.error('add to cart error:', error)
      commit(BasketMutationTypes.SET_LOADING, false)
      throw new Error(error.message)
    }
  },
  async [BasketActionTypes.REMOVE_ITEM] ({ commit }, dto: IRemoveFromBasketRequest) {
    commit(BasketMutationTypes.SET_LOADING, true)
    const { data } = await apolloClient.mutate<IRemoveFromBasketResponse, IRemoveFromBasketRequest>({
      mutation: fetchRemoveCardItemMutation,
      variables: {
        cart_id: dto.cart_id,
        cart_item_id: dto.cart_item_id
      }
    })
    if (data?.removeItemFromCart?.cart?.items) {
      commit(BasketMutationTypes.SET_CART_QUERY, { items: data?.removeItemFromCart?.cart?.items })
      commit(BasketMutationTypes.SET_LOADING, false)
    }
  },
  async [BasketActionTypes.REMOVE_ALL_ITEMS] ({ commit }, dto: IRemoveAllBasketItemsRequest) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      const { data } = await apolloClient.mutate<IRemoveAllBasketItemsResponse, IRemoveAllBasketItemsRequest>({
        mutation: fetchRemoveAllCardItemSMutation,
        variables: {
          cart_id: dto.cart_id
        }
      })
      if (data?.removeAllItems?.message) {
        commit(BasketMutationTypes.SET_CART_QUERY, { items: [] })
        commit(BasketMutationTypes.SET_BASKET_ITEMS, [])
      }
      commit(BasketMutationTypes.SET_LOADING, false)
    } catch (error: any) {
      console.error(error)
      commit(BasketMutationTypes.SET_LOADING, false)
      throw new Error(error.message)
    }
  },
  async [BasketActionTypes.EMPTY_FREE_BEER] ({ commit }, dto: IRemoveEmptyFreeBeerRequest) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      const { data } = await apolloClient.mutate<IRemoveEmptyFreeBeerResponse, IRemoveEmptyFreeBeerRequest>({
        mutation: fetchEmptyFreeBeerQuery,
        variables: {
          cart_id: dto.cart_id
        }
      })
      if (data?.emptyFreeBeerAllItems?.message) {
        localStorage.setItem('emptyFreeBeer', 'true')
        commit(BasketMutationTypes.SET_CART_QUERY, { items: [] })
        commit(BasketMutationTypes.SET_BASKET_ITEMS, [])
      }
      commit(BasketMutationTypes.SET_LOADING, false)
    } catch (error: any) {
      console.error(error)
      throw new Error(error.message)
      commit(BasketMutationTypes.SET_LOADING, false)
    }
  },

  async [BasketActionTypes.UPDATE_ITEMS] ({ commit, state }, dto: IUpdateItemsRequest) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      const { data } = await apolloClient.mutate<IUpdateItemsResponse, IUpdateItemsRequest>({
        mutation: fetchUpdateCartItemsMutation,
        variables: {
          cart_id: dto.cart_id,
          cart_items: dto.cart_items
        }
      })

      if (data) {
        const updated = [] as any
        data.updateCartItems.cart = parseCartQuery(data?.updateCartItems.cart)
        for (let i = 0; i < state.items?.length; i += 1) {
          for (let j = 0; j < data?.updateCartItems?.cart?.items?.length; j += 1) {
            if (state.items[i].id === data.updateCartItems.cart.items[j].id) {
              updated.push({
                id: state.items[i].id,
                product: state.items[i].product,
                number_of_layer: data.updateCartItems.cart.items[j].number_of_layer,
                number_of_layer_total: data.updateCartItems.cart.items[j].number_of_layer_total,
                number_of_packages: data.updateCartItems.cart.items[j].number_of_packages,
                number_of_pallet: data.updateCartItems.cart.items[j].number_of_pallet,
                number_of_layer_freebeer: data.updateCartItems.cart.items[j].number_of_layer_freebeer,
                is_free_beer: data.updateCartItems.cart.items[j].is_free_beer,
                quantity: data.updateCartItems.cart.items[j].quantity,
                quantity_total: data.updateCartItems.cart.items[j].quantity_total,
                prices: {
                  ...data.updateCartItems.cart.items[j].prices,
                  favourites: state.items[i].prices.favourites
                }
              })
            }
          }
        }
        commit(BasketMutationTypes.SET_BASKET_ITEMS, updated)
        commit(BasketMutationTypes.SET_LOADING, false)
      }
    } catch (error) {
      commit(BasketMutationTypes.SET_LOADING, false)
      commit(BasketMutationTypes.SET_ERROR, error as IError[])
      console.error('update items error:', error)
    }
  },
  async [BasketActionTypes.UPDATE_PRICES] ({ commit, rootGetters }, dto: IUpdatePricesRequest) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      const { data } = await apolloClient.mutate<IUpdatePricesResponse, IUpdatePricesRequest>({
        mutation: fetchUpdatePricesMutation,
        variables: {
          cart_id: dto.cart_id,
          delivery_date: dto.delivery_date,
          date_popup: dto.date_popup,
          payment_method: dto.payment_method,
          outlet_order_type: dto.outlet_order_type,
          delivery_method: dto.delivery_method,
          flexDelivery: dto.flexDelivery,
          payment_method_title: dto.payment_method_title,
          note_for_driver: dto.note_for_driver,
          customer_order_number: dto.customer_order_number,
          empties_return: dto.empties_return,
          empties_data: dto.empties_data,
          no_empties_return: dto.no_empties_return ? dto.no_empties_return : '',
          delivery_hour_from: dto.delivery_hour_from,
          delivery_hour_to: dto.delivery_hour_to,
          user_name: dto.user_name,
          sur_name: dto.sur_name,
          role: dto.role,
          use_integration: dto.use_integration
        }
      })
      if (data?.updatePrices) {
        commit(BasketMutationTypes.SET_PRICES, data?.updatePrices)
        if (data?.updatePrices?.empties_arr != null) {
          if (rootGetters['main/getRoute']?.name !== 'emptiesSummary') {
            let blgaProducts = [] as Empties[]
            const defaultEmpties = cloneDeep(data?.updatePrices?.empties_arr?.empties)?.map((item: Empties) => ({
              ...item,
              number_of_units: Number(item.number_of_units)
            }))
            if (data?.updatePrices?.cart_blga_arr?.length > 0) {
              blgaProducts = cloneDeep(data?.updatePrices?.cart_blga_arr)?.map((blga: Empties) => ({
                ...blga,
                number_of_units: Number(blga.number_of_units)
              }))
            }
            const result =
              blgaProducts?.length > 0
                ? defaultEmpties?.length > 0
                  ? blgaProducts.concat(defaultEmpties)
                  : blgaProducts
                : defaultEmpties
            commit(BasketMutationTypes.SET_DEFAULT_EMPTIES, result)
          }
          commit(BasketMutationTypes.SET_EMPTIES, data?.updatePrices?.empties_arr?.empties)
        }
        commit(BasketMutationTypes.SET_LOADING, false)
      }
    } catch (error) {
      commit(BasketMutationTypes.SET_LOADING, false)
      commit(BasketMutationTypes.SET_ERROR, error as IError[])
      console.error('update prices error:', error)
    }
  },
  async [BasketActionTypes.FETCH_SHIPPING_ADDRESS] ({ commit }, dto: ISetShippingAddressRequest) {
    try {
      const { data } = await apolloClient.mutate<ISetShippingAddressResponse, ISetShippingAddressRequest>({
        mutation: fetchShippingAddressMutation,
        variables: {
          cart_id: dto.cart_id,
          shipping_addresses: dto.shipping_addresses
        }
      })
      commit(BasketMutationTypes.SET_SHIPPING_ADDRESS, data?.setShippingAddressesOnCart)
    } catch (error) {
      console.error('shipping address error', error)
    }
  },
  async [BasketActionTypes.FETCH_SHIPPING_METHODS] ({ commit }, dto: ISetShippingMethodRequest) {
    try {
      const { data } = await apolloClient.mutate<ISetShippingMethodResponse, ISetShippingMethodRequest>({
        mutation: fetchShippingMethodMutation,
        variables: {
          cart_id: dto.cart_id,
          shipping_methods: dto.shipping_methods
        }
      })
      const method = data?.setShippingMethodsOnCart?.cart.shipping_addresses as any
      if (typeof method !== 'undefined') {
        commit(BasketMutationTypes.SET_SHIPPING_METHODS, method[0]?.selected_shipping_method)
      }
    } catch (error) {
      console.error('shipping methods error', error)
    }
  },
  async [BasketActionTypes.FETCH_TRANSPORT_TYPE] ({ commit }, dto: ISetTransportTypeRequest) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      const isOntrade = localStorage.getItem('customertype')
      let transportType = ''
      if (isOntrade?.toLowerCase() === 'ontrade') {
        transportType = 'flatrate_flatrate'
      } else {
        transportType = dto.transporttype !== '' ? dto.transporttype : (localStorage.getItem('shippingMethod') || '')
      }
      const { data } = await apolloClient.mutate<ISetTransportTypeResponse, ISetTransportTypeRequest>({
        mutation: fetchTransportTypeMutation,
        variables: {
          transporttype: transportType,
          cart_id: dto.cart_id,
          outletid: dto.outletid,
          datepopup: dto.datepopup,
          store: localStorage.getItem('store') as string,
          sappaymentmethod: (dto.sappaymentmethod as string || ''),
          sappaymentcode: (dto.sappaymentcode as string || '')
        }
      })
      if (data?.setTransportType?.message) {
        commit(BasketMutationTypes.SET_TRANSPORT_TYPE, transportType)
        localStorage.setItem('shippingMethod', transportType)
        localStorage.setItem('shippingMethodSetted', 'true')
      }
      commit(BasketMutationTypes.SET_LOADING, false)
    } catch (error) {
      commit(BasketMutationTypes.SET_ERROR, error as IError[])
      console.error('transport type error', error)
      commit(BasketMutationTypes.SET_LOADING, false)
    }
    commit(BasketMutationTypes.SET_LOADING, false)
  },
  async [BasketActionTypes.FETCH_AUTOPAY_PAYMENTS] ({ commit }, dto: {cartId: string}) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      const { data } = await apolloClient.mutate<CartAutopayResponse, {cartId: string}>({
        mutation: fetchAutopayPaymentMethodsQuery,
        variables: {
          cartId: dto.cartId
        }
      })
      commit(BasketMutationTypes.SET_AVAILABLE_PAYMENT_METHODS, data?.cart.available_payment_methods)
      commit(BasketMutationTypes.SET_LOADING, false)
    } catch (error) {
      commit(BasketMutationTypes.SET_ERROR, error as IError[])
      console.error('Autopay payments error', error)
      commit(BasketMutationTypes.SET_LOADING, false)
    }
    commit(BasketMutationTypes.SET_LOADING, false)
  },
  async [BasketActionTypes.SET_AUTOPAY_PAYMENT] ({ commit }, dto: SetAutopayPaymentRequest) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      await apolloClient.mutate<CartAutopayResponse, SetAutopayPaymentRequest>({
        mutation: setAutopayPayment,
        variables: {
          cartId: dto.cartId,
          code: dto.code,
          backUrl: dto.backUrl,
          agreementsIds: dto.agreementsIds || ''
        }
      })
      commit(BasketMutationTypes.SET_LOADING, false)
    } catch (error) {
      commit(BasketMutationTypes.SET_ERROR, error as IError[])
      console.error('Autopay payments error', error)
      commit(BasketMutationTypes.SET_LOADING, false)
    }
    commit(BasketMutationTypes.SET_LOADING, false)
  },
  async [BasketActionTypes.PLACE_ORDER] ({ commit }, dto: IPlaceOrderRequest) {
    commit(BasketMutationTypes.SET_LOADING, true)
    try {
      const { data } = await apolloClient.mutate<IPlaceOrderResponse, IPlaceOrderRequest>({
        mutation: fetchPlaceOrderMutation,
        variables: {
          cart_id: dto.cart_id,
          custom_email: dto.custom_email,
          deliveryaftersixpm: dto.deliveryaftersixpm,
          deliveryonpallet: dto.deliveryonpallet,
          notefordriver: dto.notefordriver,
          sappaymentmethod: dto.sappaymentmethod,
          payment_id: dto.payment_id,
          payment_method: dto.payment_method,
          customerordernumber: dto.customerordernumber,
          delivery_hour_from: dto.delivery_hour_from,
          delivery_hour_to: dto.delivery_hour_to,
          sap_surname: dto.sap_surname,
          sap_name: dto.sap_name,
          role: dto.role
        }
      })
      if (data) {
        commit(BasketMutationTypes.SET_ORDER, data?.placeOrder?.order)
        commit(BasketMutationTypes.SET_BASKET_ITEMS, [])
        localStorage.removeItem('customerOrderNumber')
        localStorage.removeItem('noteForDriver')
        localStorage.removeItem('deliveryHourStartTime')
        localStorage.removeItem('deliveryHourEndTime')
      }
      commit(BasketMutationTypes.SET_ERROR, [])
    } catch (error: any) {
      commit(BasketMutationTypes.SET_ERROR, [error?.message])
      commit(BasketMutationTypes.SET_LOADING, false)
      console.error('place order error', error)
    }
  },
  async [BasketActionTypes.FETCH_AUTOPAY_REDIRECTION] ({ commit }, dto: {orderNumber: string}) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      const data = await apolloClient.mutate<any, {orderNumber: string}>({
        mutation: fetchAutopayRedirection,
        variables: {
          orderNumber: dto.orderNumber
        }
      })
      commit(BasketMutationTypes.SET_LOADING, false)
      return data
    } catch (error) {
      commit(BasketMutationTypes.SET_ERROR, error as IError[])
      console.error('Autopay payments error', error)
      commit(BasketMutationTypes.SET_LOADING, false)
    }
    commit(BasketMutationTypes.SET_LOADING, false)
  },
  async [BasketActionTypes.FETCH_AUTOPAY_PAYMENT_STATUS] ({ commit }, dto: {orderNumber: string, hash: string}) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      const data = await apolloClient.mutate<any, {orderNumber: string, hash: string}>({
        mutation: fetchAutopayPaymentStatus,
        variables: {
          orderNumber: dto.orderNumber,
          hash: dto.hash
        }
      })
      commit(BasketMutationTypes.SET_LOADING, false)
      return data.data
    } catch (error) {
      commit(BasketMutationTypes.SET_ERROR, error as IError[])
      console.error('Autopay payments error', error)
      commit(BasketMutationTypes.SET_LOADING, false)
    }
    commit(BasketMutationTypes.SET_LOADING, false)
  },
  async [BasketActionTypes.SAVE_ORDER_FOR_LATER] (
    { commit },
    { cartId, noteForDriver, customerOrderNumber }: IAddSaveOrderForLaterRequest
  ) {
    try {
      const { data } = await apolloClient.query<IAddSaveOrderForLaterResponse, IAddSaveOrderForLaterRequest>({
        query: addSaveOrderForLaterQuery,
        variables: {
          cartId,
          noteForDriver,
          customerOrderNumber
        }
      })
      commit(BasketMutationTypes.SET_BASKET_ITEMS, [])
      return data
    } catch (error) {
      console.error(error)
    }
  },
  async [BasketActionTypes.CHECK_ATP] ({ commit, rootGetters }, dto: ICheckATP) {
    try {
      const { data } = await apolloClient.query<ICheckATPResponse, ICheckATP>({
        query: checkATPQuery,
        variables: {
          products: dto.products
        }
      })
      return data
    } catch (error) {
      console.error(error)
    }
  },
  async [BasketActionTypes.SAVE_ORDER_AS_PENDING] (
    { commit },
    { cartId, shipToPreOrder, sapOrderType, customerOrderNumber }: IAddSaveOrderAsPendingRequest
  ) {
    try {
      const { data } = await apolloClient.query<IAddSaveOrderAsPendingResponse, IAddSaveOrderAsPendingRequest>({
        query: addSaveOrderAsPendingQuery,
        variables: {
          cartId,
          shipToPreOrder,
          sapOrderType,
          customerOrderNumber
        }
      })
      commit(BasketMutationTypes.SET_BASKET_ITEMS, [])
      return data
    } catch (error) {
      console.error(error)
    }
  },
  async [BasketActionTypes.CREATE_SUGGESTED_CART] ({ commit }, dto: ICreateSuggestedCartRequest) {
    try {
      commit(BasketMutationTypes.SET_LOADING, true)
      const { data } = await apolloClient.query<ICreateSuggestedCartResponse, ICreateSuggestedCartRequest>({
        query: createSuggestedCartQuery,
        variables: {
          shipTo: dto.shipTo,
          cartId: dto?.cartId
        }
      })

      if (data) {
        commit(BasketMutationTypes.SET_BASKET_ID, data.cartId)
      }
      commit(BasketMutationTypes.SET_LOADING, false)
    } catch (error) {
      console.error(error)
      commit(BasketMutationTypes.SET_ERROR, [error as IError])
      commit(BasketMutationTypes.SET_LOADING, false)
    }
  }
}

const BasketModule = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

function parseCartQuery (cart: BaseCartQueryResponse | any) {
  cart?.items?.map((item: BaseCartQueryItem) => {
    item.prices.price_details = JSON.parse(item.prices.price_details as unknown as string)
    item.prices.multi_deposit_details = JSON.parse(item.prices.multi_deposit_details as unknown as string)
    if (item.product?.bundle_price_details != null) {
      item.product.bundle_price_details = JSON.parse(item.product.bundle_price_details as string)
    }
    return item
  })
  cart.grand_total_your_price_details = JSON.parse(cart.grand_total_your_price_details as string)
  return cart
}

export default BasketModule
